/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React, { useState, useRef } from 'react';
import { 
  X, 
  FileText, 
  Download, 
  Trash2, 
  Upload, 
  Plus, 
  Eye, 
  Clock, 
  Paperclip, 
  FileCheck2, 
  Calendar, 
  History, 
  Award,
  Building2,
  FileSpreadsheet,
  CheckCircle2,
  Image as ImageIcon
} from 'lucide-react';
import { CertificateRecord, CertificateAttachment, CertificateHistoryEntry } from '../types.ts';

interface CertFilesHistoryModalProps {
  isOpen: boolean;
  onClose: () => void;
  certificate: CertificateRecord | null;
  onSaveCertificate: (updated: CertificateRecord) => void;
}

export const CertFilesHistoryModal: React.FC<CertFilesHistoryModalProps> = ({
  isOpen,
  onClose,
  certificate,
  onSaveCertificate
}) => {
  const [activeTab, setActiveTab] = useState<'files' | 'timeline' | 'upload'>('files');
  const [previewFile, setPreviewFile] = useState<CertificateAttachment | null>(null);
  const [uploadNote, setUploadNote] = useState<string>('');
  const [uploaderName, setUploaderName] = useState<string>('Auditor HSEQ / Calidad');
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  if (!isOpen || !certificate) return null;

  const attachments = certificate.attachments || [];
  const historyEntries = certificate.history || [];

  // Helper to get icon for file type
  const getFileIcon = (fileType: string) => {
    switch (fileType) {
      case 'pdf':
        return <FileText className="w-5 h-5 text-red-400" />;
      case 'word':
        return <FileText className="w-5 h-5 text-sky-400" />;
      case 'powerpoint':
        return <FileText className="w-5 h-5 text-orange-400" />;
      case 'image':
        return <ImageIcon className="w-5 h-5 text-emerald-400" />;
      case 'excel':
        return <FileSpreadsheet className="w-5 h-5 text-emerald-400" />;
      default:
        return <Paperclip className="w-5 h-5 text-slate-400" />;
    }
  };

  // Helper to determine file type from extension
  const detectFileType = (fileName: string): 'pdf' | 'word' | 'powerpoint' | 'image' | 'excel' | 'other' => {
    const ext = fileName.split('.').pop()?.toLowerCase() || '';
    if (ext === 'pdf') return 'pdf';
    if (['doc', 'docx'].includes(ext)) return 'word';
    if (['ppt', 'pptx', 'pps', 'ppsx'].includes(ext)) return 'powerpoint';
    if (['jpg', 'jpeg', 'png', 'webp', 'svg', 'gif'].includes(ext)) return 'image';
    if (['xls', 'xlsx', 'csv'].includes(ext)) return 'excel';
    return 'other';
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  // Handle direct file upload
  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    const newAttachments: CertificateAttachment[] = [];
    const newHistory: CertificateHistoryEntry[] = [];

    Array.from(files).forEach((file: File) => {
      const reader = new FileReader();
      const detectedType = detectFileType(file.name);

      reader.onload = (event) => {
        const dataUrl = event.target?.result as string;
        const attachmentId = `att_${Date.now()}_${Math.random().toString(36).substring(2, 7)}`;
        
        const newAtt: CertificateAttachment = {
          id: attachmentId,
          name: file.name,
          fileType: detectedType,
          mimeType: file.type || 'application/octet-stream',
          sizeBytes: file.size,
          sizeFormatted: formatFileSize(file.size),
          dataUrl: dataUrl,
          uploadedAt: new Date().toISOString(),
          uploadedBy: uploaderName.trim() || 'Auditor HSEQ',
          notes: uploadNote.trim() || `Documento cargado el ${new Date().toLocaleDateString('es-MX')}`,
          versionNumber: (attachments.length + newAttachments.length + 1)
        };

        const histEntry: CertificateHistoryEntry = {
          id: `hist_${Date.now()}_${Math.random().toString(36).substring(2, 7)}`,
          date: new Date().toISOString(),
          type: 'archivo_adjuntado',
          title: `Carga de archivo: ${file.name}`,
          description: `Se adjuntó nuevo documento de evidencia (${formatFileSize(file.size)}) por ${uploaderName.trim() || 'Auditor HSEQ'}. ${uploadNote.trim() ? `Nota: ${uploadNote.trim()}` : ''}`,
          user: uploaderName.trim() || 'Auditor HSEQ',
          attachmentId: attachmentId,
          attachmentName: file.name
        };

        newAttachments.push(newAtt);
        newHistory.push(histEntry);

        // If processed all files
        if (newAttachments.length === files.length) {
          const updatedCert: CertificateRecord = {
            ...certificate,
            attachments: [...(certificate.attachments || []), ...newAttachments],
            history: [...(certificate.history || []), ...newHistory],
            updatedAt: new Date().toISOString()
          };
          onSaveCertificate(updatedCert);
          setUploadNote('');
          setActiveTab('files');
        }
      };

      reader.readAsDataURL(file);
    });

    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Handle Download File
  const handleDownloadFile = (att: CertificateAttachment) => {
    if (!att.dataUrl) {
      alert('El archivo no cuenta con contenido descargable.');
      return;
    }
    const anchor = document.createElement('a');
    anchor.href = att.dataUrl;
    anchor.download = att.name || `Certificado_${certificate.code}_${att.id}`;
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
  };

  // Handle Delete File
  const handleDeleteFile = (attachmentId: string, attachmentName: string) => {
    if (!window.confirm(`¿Está seguro de eliminar el archivo "${attachmentName}"?`)) {
      return;
    }

    const updatedAttachments = attachments.filter(a => a.id !== attachmentId);
    const histEntry: CertificateHistoryEntry = {
      id: `hist_${Date.now()}_${Math.random().toString(36).substring(2, 7)}`,
      date: new Date().toISOString(),
      type: 'observacion',
      title: `Eliminación de archivo: ${attachmentName}`,
      description: `El documento fue retirado del expediente de la certificación por ${uploaderName || 'Auditor HSEQ'}.`,
      user: uploaderName || 'Auditor HSEQ'
    };

    const updatedCert: CertificateRecord = {
      ...certificate,
      attachments: updatedAttachments,
      history: [...historyEntries, histEntry],
      updatedAt: new Date().toISOString()
    };

    onSaveCertificate(updatedCert);
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-xs flex items-center justify-center p-3 sm:p-4 overflow-y-auto">
      <div className="bg-[#14151a] border border-[#2d2f3d] rounded-2xl max-w-4xl w-full text-white shadow-2xl overflow-hidden flex flex-col max-h-[90vh] my-auto">
        
        {/* HEADER */}
        <div className="p-4 sm:p-5 bg-[#1a1c24] border-b border-[#2d2f3d] flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-indigo-950/80 border border-indigo-500/50 flex items-center justify-center text-indigo-400 shadow-md">
              <History className="w-5 h-5" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <span className="text-xs font-mono font-bold text-amber-400 bg-amber-950/60 px-2 py-0.5 rounded border border-amber-600/40">
                  {certificate.code}
                </span>
                <h3 className="text-base sm:text-lg font-bold text-white">
                  Expediente Digital e Histórico de Archivos
                </h3>
              </div>
              <p className="text-xs text-slate-400 mt-0.5">
                {certificate.name} &bull; Emisor: <strong className="text-slate-300">{certificate.issuer}</strong> &bull; Vigencia: <strong className="text-amber-300">{certificate.validUntil}</strong>
              </p>
            </div>
          </div>

          <button
            onClick={onClose}
            className="p-1.5 rounded-lg text-slate-400 hover:text-white hover:bg-[#252836] transition-colors cursor-pointer"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* TABS */}
        <div className="flex border-b border-[#2d2f3d] bg-[#161720] px-5 gap-4">
          <button
            onClick={() => setActiveTab('files')}
            className={`py-3 text-xs font-bold flex items-center gap-2 border-b-2 transition-all cursor-pointer ${
              activeTab === 'files'
                ? 'border-indigo-500 text-indigo-400'
                : 'border-transparent text-slate-400 hover:text-slate-200'
            }`}
          >
            <Paperclip className="w-3.5 h-3.5" />
            <span>Archivos Adjuntos ({attachments.length})</span>
          </button>

          <button
            onClick={() => setActiveTab('upload')}
            className={`py-3 text-xs font-bold flex items-center gap-2 border-b-2 transition-all cursor-pointer ${
              activeTab === 'upload'
                ? 'border-indigo-500 text-indigo-400'
                : 'border-transparent text-slate-400 hover:text-slate-200'
            }`}
          >
            <Upload className="w-3.5 h-3.5" />
            <span>Subir Nuevos Documentos (PDF, PPTX, DOCX, Img)</span>
          </button>

          <button
            onClick={() => setActiveTab('timeline')}
            className={`py-3 text-xs font-bold flex items-center gap-2 border-b-2 transition-all cursor-pointer ${
              activeTab === 'timeline'
                ? 'border-indigo-500 text-indigo-400'
                : 'border-transparent text-slate-400 hover:text-slate-200'
            }`}
          >
            <Clock className="w-3.5 h-3.5" />
            <span>Línea de Tiempo y Auditorías ({historyEntries.length})</span>
          </button>
        </div>

        {/* BODY */}
        <div className="p-4 sm:p-5 overflow-y-auto space-y-4 flex-1 text-xs">
          
          {/* TAB 1: ARCHIVOS ADJUNTOS */}
          {activeTab === 'files' && (
            <div className="space-y-3">
              {attachments.length === 0 ? (
                <div className="p-8 text-center bg-[#181920] rounded-xl border border-[#272936] space-y-3">
                  <FileText className="w-10 h-10 text-slate-600 mx-auto" />
                  <div className="space-y-1">
                    <h4 className="font-bold text-white text-sm">No hay documentos adjuntos aún</h4>
                    <p className="text-slate-400 max-w-md mx-auto">
                      Suba certificados oficiales en PDF, presentaciones ejecutivas en PowerPoint, dictámenes en Word o evidencias fotográficas.
                    </p>
                  </div>
                  <button
                    onClick={() => setActiveTab('upload')}
                    className="px-4 py-2 bg-indigo-600 hover:bg-indigo-500 text-white rounded-xl text-xs font-bold cursor-pointer inline-flex items-center gap-1.5 shadow-md"
                  >
                    <Upload className="w-3.5 h-3.5" />
                    Subir Primer Documento
                  </button>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {attachments.map((att) => (
                    <div 
                      key={att.id} 
                      className="p-3.5 rounded-xl bg-[#181920] border border-[#272936] hover:border-indigo-500/50 transition-all flex flex-col justify-between gap-3 group shadow-md"
                    >
                      <div className="flex items-start gap-3">
                        <div className="p-2.5 rounded-xl bg-[#111216] border border-[#2e303f] shrink-0">
                          {getFileIcon(att.fileType)}
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2">
                            <span className="text-[10px] font-mono uppercase font-bold text-indigo-300 bg-indigo-950/70 px-1.5 py-0.2 rounded border border-indigo-700/50">
                              {att.fileType}
                            </span>
                            <span className="text-[10px] text-slate-400 font-mono">
                              {att.sizeFormatted || 'N/A'}
                            </span>
                            {att.versionNumber && (
                              <span className="text-[10px] font-bold text-slate-300 bg-slate-800 px-1.5 py-0.2 rounded">
                                v{att.versionNumber}
                              </span>
                            )}
                          </div>
                          <h4 className="font-bold text-white text-xs mt-1 truncate" title={att.name}>
                            {att.name}
                          </h4>
                          {att.notes && (
                            <p className="text-[11px] text-slate-400 mt-1 line-clamp-2 italic">
                              "{att.notes}"
                            </p>
                          )}
                          <div className="text-[10px] text-slate-500 mt-2 flex items-center gap-2">
                            <span>Subido el: {att.uploadedAt ? att.uploadedAt.split('T')[0] : 'Reciente'}</span>
                            <span>&bull;</span>
                            <span>Por: {att.uploadedBy || 'Auditor'}</span>
                          </div>
                        </div>
                      </div>

                      {/* Actions */}
                      <div className="pt-2 border-t border-[#232532] flex items-center justify-between gap-2">
                        {att.fileType === 'image' && (
                          <button
                            type="button"
                            onClick={() => setPreviewFile(att)}
                            className="px-2.5 py-1 rounded-lg bg-[#242635] hover:bg-indigo-600 text-slate-300 hover:text-white font-semibold text-[11px] flex items-center gap-1.5 cursor-pointer transition-colors"
                          >
                            <Eye className="w-3 h-3" />
                            <span>Ver Imagen</span>
                          </button>
                        )}
                        <button
                          type="button"
                          onClick={() => handleDownloadFile(att)}
                          className="px-3 py-1 rounded-lg bg-indigo-600/30 hover:bg-indigo-600 text-indigo-300 hover:text-white font-semibold text-[11px] flex items-center gap-1.5 cursor-pointer transition-colors border border-indigo-500/40 ml-auto"
                        >
                          <Download className="w-3 h-3" />
                          <span>Descargar</span>
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDeleteFile(att.id, att.name)}
                          className="p-1 rounded-lg text-slate-500 hover:text-red-400 hover:bg-red-950/50 cursor-pointer transition-colors"
                          title="Eliminar archivo del expediente"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* TAB 2: SUBIR DOCUMENTOS */}
          {activeTab === 'upload' && (
            <div className="space-y-4 max-w-2xl mx-auto">
              <div className="p-6 rounded-2xl border-2 border-dashed border-indigo-500/40 bg-indigo-950/10 text-center space-y-3 hover:border-indigo-400 transition-colors">
                <div className="w-12 h-12 rounded-2xl bg-indigo-950/80 border border-indigo-500/50 flex items-center justify-center text-indigo-400 mx-auto shadow-lg">
                  <Upload className="w-6 h-6" />
                </div>
                <div className="space-y-1">
                  <h4 className="font-bold text-white text-sm">
                    Seleccione o arrastre los archivos de la certificación
                  </h4>
                  <p className="text-slate-400 text-xs">
                    Formatos admitidos: <strong>PDF (.pdf)</strong>, <strong>PowerPoint (.pptx, .ppt)</strong>, <strong>Word (.docx, .doc)</strong>, <strong>Excel (.xlsx)</strong> e <strong>Imágenes (.png, .jpg, .svg)</strong>.
                  </p>
                </div>

                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleFileUpload}
                  multiple
                  accept=".pdf,.doc,.docx,.ppt,.pptx,.xls,.xlsx,.png,.jpg,.jpeg,.webp,.svg"
                  className="hidden"
                />

                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  className="px-5 py-2.5 rounded-xl bg-gradient-to-r from-indigo-600 to-indigo-700 hover:from-indigo-500 hover:to-indigo-600 text-white font-bold text-xs shadow-lg shadow-indigo-950/50 inline-flex items-center gap-2 cursor-pointer transition-all"
                >
                  <Plus className="w-4 h-4" />
                  <span>Examinar Archivos en su Equipo</span>
                </button>
              </div>

              {/* Form Metadata */}
              <div className="p-4 bg-[#181920] rounded-xl border border-[#272936] space-y-3">
                <h5 className="font-bold text-white text-xs uppercase tracking-wider text-slate-300">
                  Metadatos de la Versión / Evidencia:
                </h5>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="text-[10px] text-slate-400 block mb-1 font-semibold">
                      Auditor / Responsable de la Carga:
                    </label>
                    <input
                      type="text"
                      value={uploaderName}
                      onChange={(e) => setUploaderName(e.target.value)}
                      placeholder="Ej: Ing. Pedro Borja / Auditor HSEQ"
                      className="w-full bg-[#101115] border border-[#2c2e3d] rounded-lg px-3 py-1.5 text-xs text-white placeholder-slate-500 focus:outline-none focus:border-indigo-500"
                    />
                  </div>

                  <div>
                    <label className="text-[10px] text-slate-400 block mb-1 font-semibold">
                      Nota o Descripción del Documento:
                    </label>
                    <input
                      type="text"
                      value={uploadNote}
                      onChange={(e) => setUploadNote(e.target.value)}
                      placeholder="Ej: Reporte de Auditoría Externa TÜV 2026..."
                      className="w-full bg-[#101115] border border-[#2c2e3d] rounded-lg px-3 py-1.5 text-xs text-white placeholder-slate-500 focus:outline-none focus:border-indigo-500"
                    />
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* TAB 3: LÍNEA DE TIEMPO E HISTORIAL */}
          {activeTab === 'timeline' && (
            <div className="space-y-3">
              {historyEntries.length === 0 ? (
                <div className="p-6 text-center text-slate-400 bg-[#181920] rounded-xl border border-[#272936]">
                  Aún no hay registros en la bitácora de auditorías de esta certificación.
                </div>
              ) : (
                <div className="relative pl-6 space-y-4 before:absolute before:left-2 before:top-2 before:bottom-2 before:w-0.5 before:bg-[#282a3a]">
                  {historyEntries.slice().reverse().map((h) => (
                    <div key={h.id} className="relative group">
                      <div className="absolute -left-6 top-1 w-3.5 h-3.5 rounded-full bg-indigo-500 border-2 border-[#14151a]" />
                      <div className="p-3.5 rounded-xl bg-[#181920] border border-[#272936] space-y-1">
                        <div className="flex items-center justify-between">
                          <h4 className="font-bold text-white text-xs">{h.title}</h4>
                          <span className="text-[10px] font-mono text-slate-400">
                            {h.date ? h.date.split('T')[0] : 'Fecha no registrada'}
                          </span>
                        </div>
                        <p className="text-slate-300 text-xs">{h.description}</p>
                        {h.user && (
                          <span className="text-[10px] text-slate-500 block font-mono">
                            Registrado por: {h.user}
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

        </div>

        {/* IMAGE PREVIEW MODAL OVERLAY */}
        {previewFile && (
          <div className="fixed inset-0 z-60 bg-black/90 flex flex-col items-center justify-center p-4">
            <div className="max-w-3xl w-full bg-[#181920] rounded-2xl border border-slate-700 p-4 space-y-3">
              <div className="flex items-center justify-between">
                <h4 className="font-bold text-white text-sm truncate">{previewFile.name}</h4>
                <button
                  onClick={() => setPreviewFile(null)}
                  className="p-1 rounded-lg text-slate-400 hover:text-white bg-slate-800"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
              <div className="max-h-[70vh] overflow-auto flex items-center justify-center bg-black/40 rounded-xl p-2">
                <img 
                  src={previewFile.dataUrl} 
                  alt={previewFile.name} 
                  className="max-h-[65vh] object-contain rounded-lg shadow-lg"
                />
              </div>
              <div className="flex justify-end">
                <button
                  onClick={() => handleDownloadFile(previewFile)}
                  className="px-4 py-2 bg-indigo-600 hover:bg-indigo-500 text-white rounded-xl text-xs font-bold inline-flex items-center gap-2"
                >
                  <Download className="w-3.5 h-3.5" />
                  Descargar Imagen
                </button>
              </div>
            </div>
          </div>
        )}

        {/* FOOTER */}
        <div className="p-4 bg-[#1a1c24] border-t border-[#2d2f3d] flex items-center justify-between">
          <span className="text-xs text-slate-400">
            Total de archivos en el expediente: <strong className="text-white">{attachments.length}</strong>
          </span>
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-xl bg-[#252836] hover:bg-slate-700 text-white text-xs font-semibold cursor-pointer transition-colors"
          >
            Cerrar Expediente
          </button>
        </div>

      </div>
    </div>
  );
};
